let tempIdCounter = 0;

function generateTempId(prefix = "msg") {
  tempIdCounter += 1;
  return `${prefix}-${Date.now()}-${tempIdCounter}`;
}

/**
 * A message a page adds to its own thread; the playbook, reports and setup
 * chats build theirs by hand. `extras` ride along on the message: an
 * answer's sources and responseTimeMs, a setup step's metadata.
 */
export function createChatMessage(role, content, extras = {}) {
  return {
    id: generateTempId(role),
    role,
    content,
    createdAt: new Date().toISOString(),
    ...extras,
  };
}

/**
 * When a message was sent, as epoch milliseconds: its createdAt or
 * timestamp, or null when it carries neither.
 */
export function getMessageTimestampMs(message) {
  const rawValue = message?.createdAt ?? message?.timestamp;
  if (!rawValue) return null;
  const timestamp =
    typeof rawValue === "number" ? rawValue : new Date(rawValue).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function normalizePart(part) {
  if (!part || typeof part !== "object") {
    return part;
  }

  if (part.type === "reasoning" && part.reasoning && !part.text) {
    const { reasoning, ...rest } = part;
    return {
      ...rest,
      type: "reasoning",
      text: part.text ?? reasoning,
    };
  }

  if (part.type === "file") {
    const mediaType = part.mediaType ?? part.mimeType;

    return {
      ...part,
      mediaType,
    };
  }

  return part;
}

export function getMessageText(message) {
  if (!message) {
    return "";
  }

  if (typeof message === "string") {
    return message;
  }

  const parts = Array.isArray(message.parts) ? message.parts : [];
  const textFromParts = parts
    .map((part) => {
      if (!part || typeof part !== "object") {
        return "";
      }

      if (part.type === "reasoning") {
        return "";
      }

      if (typeof part.text === "string") {
        return part.text;
      }

      if (typeof part.value === "string") {
        return part.value;
      }

      if (typeof part.output === "string") {
        return part.output;
      }

      if (typeof part.delta === "string") {
        return part.delta;
      }

      return "";
    })
    .filter(Boolean)
    .join(" ");

  if (textFromParts) {
    return textFromParts;
  }

  // If parts contained reasoning, don't fall back to message.content which may include reasoning text
  const hasReasoningPart = parts.some(
    (part) => part && typeof part === "object" && part.type === "reasoning"
  );
  if (hasReasoningPart) {
    return "";
  }

  if (typeof message.content === "string") {
    return message.content;
  }

  if (Array.isArray(message.content)) {
    return message.content
      .map((part) => part?.text || "")
      .filter(Boolean)
      .join(" ");
  }

  if (typeof message.text === "string") {
    return message.text;
  }

  return "";
}

/**
 * The last user message that says something, or null. A chat route answers
 * this one and records the turn's feedback under its id.
 */
export function getLatestUserMessage(messages) {
  if (!Array.isArray(messages)) return null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === "user" && getMessageText(message)?.trim()) return message;
  }
  return null;
}

export function toUiMessage(message) {
  if (!message || typeof message !== "object") {
    return message;
  }

  if (Array.isArray(message.parts)) {
    const parts = message.parts.map(normalizePart);
    const content = getMessageText({ ...message, parts });

    return {
      id: message.id ?? generateTempId("msg"),
      ...message,
      parts,
      content,
    };
  }

  const content = getMessageText(message);
  const parts = content
    ? [
        {
          type: "text",
          text: content,
        },
      ]
    : [];

  return {
    id: message.id ?? generateTempId("msg"),
    role: message.role ?? "user",
    parts,
    content,
    ...message,
  };
}

export function toLegacyMessage(message) {
  if (!message || typeof message !== "object") {
    return message;
  }

  const uiMessage = toUiMessage(message);
  const content = getMessageText(uiMessage);

  return {
    ...uiMessage,
    content,
  };
}

// Streaming renders are throttled, so a list read when a reply finishes can
// still hold that reply without its last chunk. Puts the finished reply's full
// text in its place: matched by id, or else the last message when it is the
// assistant's and holds the start of the finished reply.
export function withFinishedReply(messages, finished) {
  if (
    !Array.isArray(messages) ||
    finished?.role !== "assistant" ||
    typeof finished.content !== "string"
  ) {
    return messages;
  }
  let index = finished.id
    ? messages.findLastIndex((message) => message?.id === finished.id)
    : -1;
  if (index === -1) {
    const last = messages[messages.length - 1];
    if (typeof last?.content === "string" && finished.content.startsWith(last.content)) {
      index = messages.length - 1;
    }
  }
  const current = messages[index];
  if (current?.role !== "assistant") {
    return messages;
  }
  if (current.content === finished.content) {
    return messages;
  }
  const next = messages.slice();
  next[index] = { ...current, content: finished.content };
  return next;
}

export function ensureUiMessages(messages) {
  if (!Array.isArray(messages)) {
    return [];
  }

  return messages.map((msg) => toUiMessage(msg));
}
